
<template>
  <div class="card mb-25 border-0 rounded-0 bg-white add-user-card">
    <div class="card-header">
      <h3 class="text-black fw-semibold">Ajouter une créance</h3>
    </div>
    <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">
      <Form ref="creanceForm" @submit="addCreance" :validation-schema="creanceSchema">
        <div class="row gx-3">
          <!-- Ligne 1 : Date + Client + Commandes -->
          <div class="col-md-4 mb-3">
            <div class="form-group">
              <label class="d-block text-black fw-semibold mb-10">
                Date de la créance <span class="text-danger">*</span>
              </label>
              <Field
                name="date"
                type="date"
                :value="getCurrentDateTime()"
                readonly
                class="form-control shadow-none fs-md-15 text-black"
              />
              <ErrorMessage name="date" class="text-danger" />
            </div>
          </div>
          
          <div class="col-md-4 mb-3">
            <div class="form-group">
              <label class="d-block text-black mb-10">
                Client <span class="text-danger">*</span>
              </label>
              <Field name="client" v-model="client" type="text" v-slot="{ field }">
                <Multiselect
                  v-model="field.value"
                  v-bind="field"
                  :options="clientOptions"
                  :preserve-search="true"
                  :multiple="false"
                  :searchable="true"
                  placeholder="Sélectionner le client"
                  label="label"
                  track-by="label"
                  @select="onClientSelect"
                />
              </Field>
              <ErrorMessage name="client" class="text-danger" />
            </div>
          </div>
          
          <div class="col-md-4 mb-3">
            <div class="form-group">
              <label class="d-block text-black mb-10">
                Commandes client <span class="text-danger">*</span>
              </label>
              <Field name="commandes" v-slot="{ field }">
                <Multiselect
                  v-model="field.value"
                  v-bind="field"
                  mode="tags"
                  :options="commandesOptions"
                  :preserve-search="true"
                  :searchable="true"
                  placeholder="Sélectionner les commandes"
                  label="label"
                  valueProp="value"
                  @update:modelValue="onCommandeSelect"
                  :disabled="!client || commandesOptions.length === 0"
                  :close-on-select="false"
                />
              </Field>
              <ErrorMessage name="commandes" class="text-danger" />
              <small class="text-muted" v-if="commandesSelectionnees.length > 0">
                {{ commandesSelectionnees.length }} commande(s) sélectionnée(s)
              </small>
            </div>
          </div>
          
          <!-- Ligne 2 : Références + Montant Total + Montant Réglé -->
          <div class="col-md-4 mb-3">
            <div class="form-group">
              <label class="d-block text-black fw-semibold mb-1">
                Références des Commandes <span class="text-danger">*</span>
              </label>
              <Field
                type="text"
                name="refCommande"
                v-model="refCommande"
                readonly
                class="form-control shadow-none fs-md-15 text-black"
              />
              <ErrorMessage name="refCommande" class="text-danger" />
            </div>
          </div>

          <div class="col-md-4 mb-3">
            <div class="form-group">
              <label class="d-block text-black fw-semibold mb-1">
                Montant total des Commandes <span class="text-danger">*</span>
              </label>
              <input
                type="text"
                name="montantTotalCommandes"
                :value="separateur(parseInt(montantFacture.replace(/\s+/g, '') || 0))"
                readonly
                class="form-control shadow-none fs-md-15 text-black bg-light"
              />
              <ErrorMessage name="montantTotalCommandes" class="text-danger" />
            </div>
          </div>
          
          <div class="col-md-4 mb-3">
            <div class="form-group">
              <label class="d-block text-black fw-semibold mb-1">
                Montant Réglé <span class="text-danger">*</span>
              </label>
              <input
                type="text"
                name="montantRegle"
                :value="separateur(parseInt(montantRegle.replace(/\s+/g, '') || 0))"
                readonly
                class="form-control shadow-none fs-md-15 text-black bg-light"
              />
              <ErrorMessage name="montantRegle" class="text-danger" />
            </div>
          </div>
          
          <!-- Ligne 3 : Montant Créance + Durée -->
          <div class="col-md-4 mb-3">
            <div class="form-group">
              <label class="d-block text-black fw-semibold mb-1">
                Montant total de la créance <span class="text-danger">*</span>
              </label>
              <input
                type="text"
                name="montant"
                :value="montant"
                readonly
                v-format-montant
                class="form-control shadow-none fs-md-15 text-black"
              />
              <ErrorMessage name="montant" class="text-danger" />
            </div>
          </div>
          
          <div class="col-md-4 mb-3">
            <div class="form-group">
              <label class="d-block text-black fw-semibold mb-10">
                Durée de créance en jours <span class="text-danger">*</span>
              </label>
              <Field
                name="dureeCreance"
                type="number"
                v-model="dureeCreance"
                class="form-control shadow-none fs-md-15 text-black"
                placeholder="Entrer la durée en jours"
              />
              <ErrorMessage name="dureeCreance" class="text-danger" />
            </div>
          </div>
          
          <!-- Ligne 4 : Mode de règlement + Nombre d'échéances -->
          <div class="col-md-6 mb-3">
            <div class="form-group">
              <label class="d-block text-black mb-10">
                Mode de paiement initialement prévu <span class="text-danger">*</span>
              </label>
              <Field name="modeReglement" type="text" v-slot="{ field }">
                <Multiselect
                  v-model="field.value"
                  v-bind="field"
                  :options="modeReglementOptions"
                  :preserve-search="true"
                  :multiple="false"
                  :searchable="true"
                  placeholder="Sélectionner un mode de paiement"
                  label="label"
                  track-by="label"
                />
              </Field>
              <ErrorMessage name="modeReglement" class="text-danger" />
            </div>
          </div>
          
          <div class="col-md-6 mb-3">
            <div class="form-group">
              <label class="d-block text-black fw-semibold mb-1">
                Nombre d'échéances / Echéancier <span class="text-danger">*</span>
              </label>
              <Field
                type="number"
                name="nbreEcheance"
                v-model="nbreEcheance"
                class="form-control shadow-none fs-md-15 text-black"
              />
              <ErrorMessage name="nbreEcheance" class="text-danger" />
            </div>
          </div>
          
          <!-- Autres informations -->
          <div class="col-md-12 mb-3">
            <div class="form-group">
              <label class="d-block text-black fw-semibold mb-1">
                Autres informations
              </label>
              <Field
                name="autresinfos"
                as="textarea"
                cols="15"
                rows="3"
                placeholder="Entrer les autres informations"
                v-slot="{ field }"
                class="form-control shadow-none rounded-0 text-black"
              >
                <textarea
                  class="form-control shadow-none rounded-0 text-black"
                  v-model="field.value"
                ></textarea>
              </Field>
              <ErrorMessage name="autresinfos" class="text-danger" />
            </div>
          </div>
              <!-- Section Échéances -->
              <div v-if="nbreEcheance > 0" class="col-md-12 mb-3">
                <div
                  class="alert txt-danger border-danger alert-dismissible fade show"
                  role="alert"
                  v-if="montantTotalEcheance !== montant && montantTotalEcheance > 0 && montant > 0"
                >
                  <i
                    data-name="clock"
                    data-tags="time,watch,alarm"
                    data-type="clock"
                    class="vue-feather vue-feather--clock"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="feather feather-clock vue-feather__content"
                    >
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                  </i>
                  <p>Le montant total des échéances doit être égal au montant total de la créance, donc revoir la répartition</p>
                  <button class="btn-close" type="button" data-bs-dismiss="alert" aria-label="Close"></button>
                </div>
                <div class="border border-primary">
                  <div class="row d-flex align-items-center justify-content-between fw-bold py-2" style="background-color: #0a59a4">
                    <div class="col-md-12">
                      <h3 class="fs-4 text-white">Échéances</h3>
                    </div>
                  </div>
                  <div class="p-3">
                    <div class="row d-flex align-items-center justify-content-between">
                      <div class="col-md-5">
                        <label class="d-block text-black fw-semibold">Date Échéance <span class="text-danger">*</span></label>
                      </div>
                      <div class="col-md-5">
                        <label class="d-block text-black fw-semibold mb-10">Montant <span class="text-danger">*</span></label>
                      </div>
                      <div class="col-md-2">
                        <label class="d-block text-black fw-semibold mb-10">Actions</label>
                      </div>
                    </div>
                    <hr class="mt-0" />
                    <div class="row" v-for="(echeance, index) in echeances" :key="index">
                      <div class="col-md-5 mb-2">
                        <div class="form-group">
                          <input
                            v-model="echeance.dateEcheance"
                            name="dateikken"
                            type="date"
                            class="form-control shadow-none fs-md-15 text-black"
                            placeholder="Saisir la date de l'échéance"
                            @change="validateEcheances(index)"
                          />
                          <div class="invalid-feedback" v-if="valideteRowEcheance(echeance.dateEcheance)">
                            La date échéance est obligatoire.
                          </div>
                          <div class="invalid-feedback" v-if="echeanceErrors[index]?.duplicate">
                            Cette date est déjà utilisée.
                          </div>
                          <div class="invalid-feedback" v-if="echeanceErrors[index]?.order">
                            La date doit être supérieure à la précédente.
                          </div>
                          <div class="invalid-feedback" v-if="echeanceErrors[index]?.beforeFacture">
                            La date doit être postérieure à la date de facture.
                          </div>
                        </div>
                      </div>
                      <div class="col-md-5 mb-2">
                        <div class="form-group">
                          <input
                            v-model="echeance.montant"
                            name="montant"
                            type="text"
                            v-format-montant
                            class="form-control shadow-none fs-md-15 text-black"
                            placeholder="Entrer le montant"
                          />
                          <div class="invalid-feedback" v-if="valideteRowEcheance(echeance.montant)">
                            Le montant est obligatoire.
                          </div>
                        </div>
                      </div>
                      <div class="col-md-2 mb-2">
                        <button
                          class="btn btn-danger transition border-0 pb-2 ps-8 pe-8"
                          type="button"
                          @click="removeRowEcheance(index)"
                        >
                          <i class="fa fa-trash-o lh-1 me-1 position-relative top-2"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Boutons -->
              <div class="col-md-12 mt-3">
                <div class="d-flex align-items-center">
                  <button
                    ref="submitButton"
                    class="btn btn-success me-3"
                    type="submit"
                    :disabled="nbreEcheance <= 0 || isDisable"
                  >
                    Ajouter une créance
                  </button>
                  <router-link to="/creances/liste-creances" class="btn btn-danger">
                    <i class="fa fa-trash-o lh-1 me-1 position-relative top-2"></i>
                    <span class="position-relative">Annuler</span>
                  </router-link>
                </div>
              </div>
            </div>
      </Form>
    </div>
  </div>
</template>


<script lang="ts">
import { defineComponent, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { Form, Field, ErrorMessage } from 'vee-validate';
import * as Yup from 'yup';
import ApiService from '@/services/ApiService';
import { error, success, separateur } from '@/utils/utils';
import Multiselect from '@vueform/multiselect';
import router from '@/router';

export default defineComponent({
  name: "AddCreance",
  components: {
    Form,
    Field,
    ErrorMessage,
    Multiselect,
  },

  setup: () => {
    const creanceSchema = Yup.object().shape({
      date: Yup.date().required('La date de créance est obligatoire'),
      client: Yup.string().required('Le client est obligatoire'),
      commandes: Yup.array()
        .min(1, 'Veuillez sélectionner au moins une commande')
        .required('Les commandes sont obligatoires'),
      nbreEcheance: Yup.number()
        .typeError('Veuillez entrer un nombre')
        .required('Le nombre d\'échéances est obligatoire')
        .min(1, 'Le nombre d\'échéances doit être supérieur à 0'),
      modeReglement: Yup.string().required('Le mode de règlement est obligatoire'),
      montant: Yup.string()
        .typeError('Veuillez entrer des chiffres')
        .test('plafond', 'Le montant de la créance dépasse le plafond autorisé', function (value) {
          if (!plafond.value) return true; // Pas de plafond défini
          const montantValue = parseInt((value || '0').replace(/\s+/g, ''));
          return montantValue <= plafond.value;
        }),
      montantRegle: Yup.string()
        .typeError('Veuillez entrer des chiffres')
        .notRequired(), 
      autresinfos: Yup.string().notRequired(),
      dureeCreance: Yup.number()
        .typeError('Veuillez entrer un nombre')
        .required('La durée de créance est obligatoire')
        .min(1, 'La durée doit être supérieure à 0'),
    });

    // Fonction pour obtenir la date et l'heure actuelles
    function getCurrentDateTime() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    // Fonction pour calculer la date d'échéance
    function calculateDueDate(startDate: string, daysToAdd: number): string {
      const date = new Date(startDate);
      date.setDate(date.getDate() + daysToAdd);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    // Variables réactives
    const clientOptions = ref([]);
    const commandesOptions = ref([]);
    const submitButton = ref<HTMLButtonElement | null>(null);
    const client = ref(null);
    const montant = ref(0);
    const nbreEcheance = ref(0);
    const echeanceErrors = ref<any[]>([]);
    const isDisable = ref(true);
    const modeReglementOptions = ref([]);
    const montantFacture = ref("0");
    const montantRegle = ref("0");
    const montantTotalEcheance = ref(0);
    const dureeCreance = ref(0); // Initialisation à 0 jours par défaut
    const dateFacture = ref(getCurrentDateTime()); // Initialisation de la date de facture
    const plafond = ref(0); // Plafond de créance
    const commandesSelectionnees = ref<any[]>([]); 
    const refCommande = ref(""); 
    // Échéances
    const echeances = reactive([{ dateEcheance: "", montant: '0' }]);

    watch(montantFacture, (newVal) => {
      montant.value = parseInt((newVal.replace(/\s+/g, '')).toString()) - parseInt((montantRegle.value.replace(/\s+/g, '')).toString());
    }, { immediate: true });

    watch(montantRegle, (newVal) => {
      montant.value = parseInt((montantFacture.value.replace(/\s+/g, '')).toString()) - parseInt((newVal.replace(/\s+/g, '')).toString());
    }, { immediate: true });

    watch(commandesSelectionnees, (newCommandes) => {
      if (!newCommandes || newCommandes.length === 0) {
        refCommande.value = '';
        return;
      }

      refCommande.value = newCommandes.map((cmdId: number) => {
        const option = commandesOptions.value.find((opt: any) => opt.value === cmdId);
        return option?.label.split(' - ')[0] || '';
      }).join(', ');
    }, { deep: true });

    const calculMontantTotal = () => {
      montantTotalEcheance.value = echeances.reduce((acc, echeance) => acc + Number(parseInt((echeance.montant.replace(/\s+/g, '')).toString()) || 0), 0);
    };

    // Valider les échéances
    const validateEcheances = (changedIndex?: number) => {
      echeanceErrors.value = echeances.map(() => ({}));
      const dates = echeances.map((e) => e.dateEcheance);

      // Vérifier les doublons
      dates.forEach((date, index) => {
        if (date && dates.indexOf(date) !== dates.lastIndexOf(date)) {
          echeanceErrors.value[index].duplicate = true;
        }
      });

      // Vérifier l'ordre croissant et la validité par rapport à dateFacture
      for (let i = 0; i < dates.length; i++) {
        if (dates[i] && dateFacture.value && new Date(dates[i]) <= new Date(dateFacture.value)) {
          echeanceErrors.value[i].beforeFacture = true;
        }
        if (i > 0 && dates[i] && dates[i - 1] && new Date(dates[i]) <= new Date(dates[i - 1])) {
          echeanceErrors.value[i].order = true;
        }
      }

      isDisable.value = echeances.some(
        (echeance, index) =>
          valideteRowEcheance(echeance.dateEcheance) ||
          valideteRowEcheance(echeance.montant) ||
          echeanceErrors.value[index]?.duplicate ||
          echeanceErrors.value[index]?.order ||
          echeanceErrors.value[index]?.beforeFacture
      );

      echeances.forEach((echeance, index) => {
        const input = document.getElementsByName("dateEcheance")[index] as HTMLInputElement;
        if (input) {
          input.classList.toggle(
            "is-invalid",
            valideteRowEcheance(echeance.dateEcheance) ||
              !!echeanceErrors.value[index].duplicate ||
              !!echeanceErrors.value[index].order ||
              !!echeanceErrors.value[index].beforeFacture
          );
        }
      });
    };

    // Supprimer une échéance
    const removeRowEcheance = (index: number) => {
      if (echeances.length > 1) echeances.splice(index, 1);
      calculMontantTotal();
      validateEcheances();
    };

    // Validation des échéances
    const valideteRowEcheance = (e: string | number) => {
      return e === "" || e === "0" || Number(e) < 0;
    };

    // Mettre à jour les échéances en fonction de nbreEcheance et dureeCreance
    const updateEcheances = () => {
      const count = parseInt(nbreEcheance.value as any) || 0;
      const interval = count > 0 ? Math.floor(dureeCreance.value / count) : 0;
      const montantParEcheance = count > 0 ? (montant.value / count).toFixed(2) : '0';

      echeances.length = count;
      for (let i = 0; i < count; i++) {
        const daysToAdd = interval * (i + 1);
        const dateEcheance = dateFacture.value ? calculateDueDate(dateFacture.value, daysToAdd) : "";
        echeances[i] = echeances[i] || { dateEcheance: "", montant: '0' };
        echeances[i].dateEcheance = dateEcheance;
        echeances[i].montant = montantParEcheance;
      }
      calculMontantTotal();
      validateEcheances();
    };

    // Surveiller nbreEcheance
    watch(nbreEcheance, () => {
      updateEcheances();
    });

    // Surveiller dureeCreance
    watch(dureeCreance, () => {
      updateEcheances();
    });

    // // Surveiller dateFacture
    // watch(dateFacture, () => {
    //   updateEcheances();
    // });

    const getAllModesReglement = async () => {
      try {
        const response = await ApiService.get('/modes-reglement');
        const modesData = response.data.data;
        modeReglementOptions.value = modesData.map((mode: any) => ({
          value: mode.id,
          label: mode.libelle,
        }));
      } catch (err) {
        error(err.message);
      }
    };

    // Charger les clients
    const getAllClients = async () => {
      try {
        const response = await ApiService.get('/all/clients');
        const clientsData = response.data.data;
        clientOptions.value = clientsData.map((client) => ({
          value: client.id + "|" + client?.contrat?.dureeCreance + "|" + client?.contrat?.plafond,
          label: client.nomClient && client.prenomClient
            ? `${client.nomClient} ${client.prenomClient}`
            : `${client.raisonSociale}`
        }));
      } catch (err) {
        error(err.message);
      }
    };

    // Gérer la sélection d'un client
    const onClientSelect = (clientValue: string) => {
      if (clientValue) {
        const clientId = parseInt(clientValue.split("|")[0]);
        dureeCreance.value = parseInt(clientValue.split("|")[1]);
        plafond.value = parseInt(clientValue.split("|")[2]);

        getCommandeByClient(clientId);
        updateEcheances(); // Mettre à jour les échéances avec la nouvelle durée
      } else {
        dureeCreance.value = 30; // Valeur par défaut
        plafond.value = 0; // Pas de plafond
        commandesOptions.value = [];
        updateEcheances();
      }
    };

     const getCommandeByClient = async (clientId: number) => {
      try {
        const response = await ApiService.get(`/commandesByClient/${clientId}`);
        const commandesData = response.data.data;
        commandesOptions.value = commandesData.map((commande: any) => ({
          value: commande.id,
          label: `${commande.refCom} - ${separateur(commande.montTTC)}`,
          montant: commande.montTTC,
          montantRegle: commande.montantRegle || 0 
        }));
      } catch (err: any) {
        error(err?.response?.data?.message || "Erreur lors du chargement des commandes");
        commandesOptions.value = [];
      }
    };

    const onCommandeSelect = (commandes: any) => {
      commandesSelectionnees.value = Array.isArray(commandes) ? commandes : [];

      if (commandesSelectionnees.value.length === 0) {
        montantFacture.value = "0";
        montantRegle.value = "0"; 
        return;
      }

      let totalCommandes = 0;
      let totalRegle = 0; 

      commandesSelectionnees.value.forEach((cmd) => {
        const option = commandesOptions.value.find((opt: any) => opt.value === cmd);
        const montant = Number(option?.montant) || 0;
        const montantRegleDuCmd = Number(option?.montantRegle) || 0;       
        totalCommandes += montant;
        totalRegle += montantRegleDuCmd; 
      });

      console.log('Total calculé:', totalCommandes, ', Total réglé:', totalRegle);
      montantFacture.value = totalCommandes.toString();
      montantRegle.value = totalRegle.toString(); 
    };

    const addCreance = async (values: any, { resetForm }: any) => {
      if (submitButton.value) {
        submitButton.value.disabled = true;
      }

      if (plafond.value > 0 && montant.value > plafond.value) {
        error(`Le montant de la créance (${montant.value}) dépasse le plafond autorisé (${plafond.value}).`);
        submitButton.value!.disabled = false;
        return;
      }


      const lastEcheanceDate = echeances.length > 0 ? echeances[echeances.length - 1].dateEcheance : '';

      const creanceData = {
        date: values.date,
        client: values.client.split("|")[0],
        commandes: values.commandes, 
        refCommande: refCommande.value,
        montant: montant.value,
        nbreEcheance: values.nbreEcheance,
        montantTotalCommandes: parseInt((montantFacture.value.replace(/\s+/g, '')).toString()),
        montantRegle: parseInt((montantRegle.value.replace(/\s+/g, '')).toString()), 
        autresinfos: values.autresinfos,
        modeReglement: values.modeReglement,
        dureeCreance: values.dureeCreance,
        dateEcheance: lastEcheanceDate, 
        echeances: echeances.map((echeance) => ({
          dateEcheance: echeance.dateEcheance,
          montant: Number(echeance.montant.replace(/\s+/g, '')),
        })),
      };

      try {
        const response = await ApiService.post("/creances", creanceData);
        if (response.data.code === 201) {
          success(response.data.message);
          resetForm();
          router.push({ name: "ListeCreance" });
        }
      } catch (err: any) {
        submitButton.value!.disabled = false;
        error(err?.response?.data?.message || "Erreur lors de la création de la créance");
      }
    };

    onMounted(() => {
      getAllClients();
      getAllModesReglement();
    });

    return {
      creanceSchema,
      addCreance,
      getCurrentDateTime,
      clientOptions,
      commandesOptions,
      commandesSelectionnees,
      submitButton,
      client,
      montant,
      nbreEcheance,
      echeances,
      calculMontantTotal,
      removeRowEcheance,
      valideteRowEcheance,
      isDisable,
      modeReglementOptions,
      montantFacture,
      montantRegle,
      montantTotalEcheance,
      echeanceErrors,
      validateEcheances,
      dureeCreance,
      dateFacture,
      plafond,
      onClientSelect,
      getCommandeByClient,
      refCommande,
      onCommandeSelect,
      separateur,
    };
  },
});
</script>

<style scoped>
.invalid-feedback {
  display: block; /* Forcer l'affichage des messages d'erreur */
}
</style>